import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  currentPushToken,
  notificationPermission,
  pushConfigured,
  requestPushToken,
  watchNotificationPermission,
} from "integrations/firebase/messaging";

import { isIosBrowserTab } from "./push-platform";
import {
  prunePushTokens,
  savePushToken,
  settledWithin,
  unregisterPushToken,
} from "./push-tokens";

/** What push can do on this device right now. */
export type PushDeviceState =
  | "unavailable" // push is not configured for this build, or nobody is signed in
  | "checking" // configured, waiting for isSupported() to answer
  | "unsupported" // this browser cannot do web push
  | "install" // iPhone/iPad browser tab: push needs the Home Screen app
  | "available" // supported and the browser has not been asked yet
  | "enabling"
  | "enabled"
  | "failed" // the last turn-on did not get a token; trying again may work
  | "disabling"
  | "off" // permission granted, but the person turned push off for this device
  | "blocked"; // the user (or the browser) denied the permission

export interface PushRegistration {
  device: PushDeviceState;
  /** Why the last turn-off did not work, until the next attempt. */
  error: string | null;
  /** Ask for permission if needed and register this device. */
  enable: () => void;
  /** Unregister this device, leaving the browser permission as it is. */
  disable: () => void;
  /**
   * The ask that makes push on by default: called from the "Register for
   * this event" click. Asks the browser for the permission in that same
   * click, and only when the browser has not been asked, push can work here
   * and this account has not turned push off on this device. Registers the
   * device once the answer is "Allow"; any other answer ends it there. It
   * never throws and never delays anything, so the registration command
   * goes out whatever happens here.
   */
  askOnRegister: () => void;
}

type Support =
  "unconfigured" | "checking" | "supported" | "install" | "unsupported";
type Phase = "idle" | "enabling" | "disabling" | "failed";
/** Push turned off on this device by this account. Per account, because the
 *  switch is one person's choice and the next account on the browser has not
 *  made it; per device, because the token it removed belongs to the browser. */
function offKey(uid: string): string {
  return `connect.push-off.${uid}`;
}
/** Set while a turn-off has not confirmed that the row or the FCM token is
 *  gone, so the next page load can finish it. */
function unregisterPendingKey(uid: string): string {
  return `connect.push-off-pending.${uid}`;
}

/** How long a turn-off waits for a registration still writing its token.
 *  Offline that write never settles; Firestore applies queued writes in
 *  order, so a delete queued after it still lands after it. */
const PENDING_REGISTRATION_WAIT_MS = 3_000;

export const TURN_ON_FAILED =
  "Couldn't turn on notifications. Check your connection and try again.";

export const TURN_OFF_FAILED =
  "Could not turn off push notifications on this device. Check your connection and try again.";

// Storage can throw (Safari private mode, blocked site data, a sandboxed
// iframe). A flag that cannot be remembered lasts for this page view; an
// exception here would cost the page.
function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // Remembered for this page view only.
  }
}

function clearFlag(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clear if storage is unreachable.
  }
}

/** What can be known without waiting for isSupported(), so a page where push
 *  is off never flashes a "checking" state. */
function initialSupport(): Support {
  if (!pushConfigured()) return "unconfigured";
  if (typeof Notification === "undefined") {
    return isIosBrowserTab() ? "install" : "unsupported";
  }
  return "checking";
}

function deviceState(
  support: Support,
  permission: NotificationPermission | null,
  phase: Phase,
  off: boolean,
): PushDeviceState {
  if (support === "unconfigured") return "unavailable";
  if (support === "checking") return "checking";
  if (support === "install") return "install";
  if (support !== "supported") return "unsupported";
  if (phase === "enabling") return "enabling";
  if (phase === "failed" && permission !== "denied") return "failed";
  if (phase === "disabling") return "disabling";
  if (permission === "granted") return off ? "off" : "enabled";
  if (permission === "denied") return "blocked";
  return "available";
}

/**
 * Device push registration for the signed-in user. Mounted once per page by
 * PushRegistrationProvider; components read it through usePushRegistration.
 *
 * Permission is only ever requested from a user gesture, because browsers
 * (iOS especially) refuse or penalise unprompted requests: from the
 * "Register for this event" click (`askOnRegister`), which is what makes
 * push on by default, and from the switch (`enable`). When permission is
 * already granted, mounting silently refreshes the token and its lastSeenAt
 * so Safari's token rotation does not strand a dead registration.
 *
 * A browser never shows the permission prompt again after "Block", so the
 * switch says where to undo it. The permission is watched: once the person
 * allows notifications again in site settings the device registers.
 *
 * Turning push off (`disable`) removes this device's token and remembers the
 * choice for this account on this browser, so the silent refresh above does
 * not register it again on the next page load. The permission stays granted,
 * because a page cannot revoke it, and that is what lets `enable` register
 * again without a prompt.
 */
function usePushRegistrationState(uid: string | null): PushRegistration {
  const [support, setSupport] = useState<Support>(initialSupport);
  const [permission, setPermission] = useState(notificationPermission);
  const [phase, setPhase] = useState<Phase>("idle");
  // The turn-off flag as this page last set it. Until it sets one, storage is
  // read on every render, so the first render for a newly signed-in uid
  // already knows, before the registration effect below can run.
  const [offOverride, setOffOverride] = useState<{
    uid: string;
    off: boolean;
  } | null>(null);
  const off =
    uid === null
      ? false
      : offOverride?.uid === uid
        ? offOverride.off
        : readFlag(offKey(uid));
  const [error, setError] = useState<string | null>(null);
  // The uid whose token this page has saved. Set only after the save, so a
  // prompt that was refused, closed or failed leaves the device free to
  // register once the permission is granted later.
  const registeredFor = useRef<string | null>(null);
  // The uid a prompt or token request is running for, so the permission
  // change our own ask causes does not start a second registration beside it.
  const busyFor = useRef<string | null>(null);
  const previousPermission = useRef(permission);
  // The registration currently writing a token, so turning off waits for it
  // rather than deleting a row it is about to write back.
  const inflight = useRef<Promise<unknown> | null>(null);

  const setOff = useCallback((forUid: string, value: boolean) => {
    if (value) writeFlag(offKey(forUid));
    else clearFlag(offKey(forUid));
    setOffOverride({ uid: forUid, off: value });
  }, []);

  useEffect(() => {
    if (support !== "checking") return;
    let cancelled = false;
    // isSupported() is async; only advertise anything once it confirms.
    void import("firebase/messaging")
      .then(({ isSupported }) => isSupported())
      .then((supported) => {
        if (cancelled) return;
        if (supported) setSupport("supported");
        else setSupport(isIosBrowserTab() ? "install" : "unsupported");
      })
      .catch(() => {
        if (!cancelled) setSupport("unsupported");
      });
    return () => {
      cancelled = true;
    };
  }, [support]);

  useEffect(() => {
    if (support !== "supported") return;
    // A change can land between the first render and this subscription.
    setPermission(notificationPermission());
    return watchNotificationPermission(() =>
      setPermission(notificationPermission()),
    );
  }, [support]);

  useEffect(() => {
    const before = previousPermission.current;
    previousPermission.current = permission;
    if (before === permission) return;
    if (permission === "granted") {
      // Allowing notifications in site settings is a choice too, and a newer
      // one than an earlier "off" on this page.
      if (uid !== null && busyFor.current !== uid) setOff(uid, false);
    }
  }, [permission, uid, setOff]);

  // A turn-off that never confirmed (the tab closed, or the network was gone
  // throughout) is finished on the next load. Both halves are idempotent.
  const retriedFor = useRef<string | null>(null);
  useEffect(() => {
    if (uid === null || support !== "supported" || !off) return;
    // Without the permission there is no token to find, and deleteToken
    // would register the SDK's default worker and fail on every load.
    if (permission !== "granted") return;
    if (!readFlag(unregisterPendingKey(uid))) return;
    if (retriedFor.current === uid || busyFor.current === uid) return;
    retriedFor.current = uid;
    void unregisterPushToken(uid).then((done) => {
      if (done) clearFlag(unregisterPendingKey(uid));
    });
  }, [uid, support, off, permission]);

  useEffect(() => {
    if (uid === null || support !== "supported" || permission !== "granted") {
      return;
    }
    if (off) return;
    if (registeredFor.current === uid || busyFor.current === uid) return;
    busyFor.current = uid;
    inflight.current = currentPushToken()
      .then(async (token) => {
        if (token !== null) {
          await savePushToken(uid, token);
          registeredFor.current = uid;
        }
        // Opportunistic tidy-up of this user's long-dead devices. Sign-out
        // removes the row for the browser in front of us; this one catches
        // the devices that were never signed out of.
        await prunePushTokens(uid);
      })
      .catch(() => undefined)
      .finally(() => {
        if (busyFor.current === uid) busyFor.current = null;
      });
  }, [uid, support, permission, off]);

  const enable = useCallback(() => {
    if (uid === null) return;
    setError(null);
    setPhase("enabling");
    busyFor.current = uid;
    setOff(uid, false);
    clearFlag(unregisterPendingKey(uid));
    inflight.current = requestPushToken()
      .then(async (token) => {
        // Re-read rather than trust the watcher: this is the moment a
        // "Block" has to turn into the hint, and a closed prompt (still
        // "default") back into a switch that can be turned on.
        setPermission(notificationPermission());
        if (token === null) {
          setPhase("idle");
          return;
        }
        await savePushToken(uid, token);
        registeredFor.current = uid;
        setPhase("idle");
      })
      .catch(() => {
        setPermission(notificationPermission());
        setPhase("failed");
      })
      .finally(() => {
        if (busyFor.current === uid) busyFor.current = null;
      });
  }, [uid, setOff]);

  const disable = useCallback(() => {
    if (uid === null) return;
    setError(null);
    setPhase("disabling");
    // Remembered first, so a reload while the delete is still running does
    // not register the device again, and finishes the delete instead.
    setOff(uid, true);
    writeFlag(unregisterPendingKey(uid));
    // This page is unregistering already; the load-time retry is for a
    // turn-off an earlier page load left unfinished.
    retriedFor.current = uid;
    const pending = inflight.current;
    inflight.current = (async () => {
      if (pending) await settledWithin(pending, PENDING_REGISTRATION_WAIT_MS);
      const done = await unregisterPushToken(uid);
      registeredFor.current = null;
      clearFlag(unregisterPendingKey(uid));
      if (!done) {
        // Still registered: say so, and let the refresh keep it current.
        setOff(uid, false);
        setError(TURN_OFF_FAILED);
      }
      setPhase("idle");
    })();
  }, [uid, setOff]);

  const device =
    uid === null ? "unavailable" : deviceState(support, permission, phase, off);

  const askOnRegister = useCallback(() => {
    if (uid === null || off || device !== "available") return;
    // Read live as well: the watcher can lag a change made in another tab.
    if (notificationPermission() !== "default") return;
    if (busyFor.current !== null) return;
    let answer: Promise<NotificationPermission> | undefined;
    try {
      // Synchronously, inside the click: Safari refuses a request that
      // follows an await, and Chrome quietens one without a gesture.
      // Nothing here may wait before this line.
      answer = Notification.requestPermission();
    } catch {
      return;
    }
    // Held while the prompt is open, so the permission change "Allow"
    // causes does not start the page-load refresh beside `enable` below.
    busyFor.current = uid;
    const release = () => {
      if (busyFor.current === uid) busyFor.current = null;
    };
    // Old Safari answers through a callback and returns undefined, so the
    // permission is re-read rather than taken from the promise alone.
    void Promise.resolve(answer)
      .then((result) => {
        release();
        const now = notificationPermission();
        setPermission(now);
        if ((result ?? now) === "granted") enable();
      })
      .catch(() => {
        release();
        setPermission(notificationPermission());
      });
  }, [uid, off, device, enable]);

  return { device, error, enable, disable, askOnRegister };
}

const PushRegistrationContext = createContext<PushRegistration | null>(null);

/** Outside a provider: component tests that mount a page on its own. Push
 *  stays off there, as it did before the provider existed. */
const NO_PUSH: PushRegistration = {
  device: "unavailable",
  error: null,
  enable: () => undefined,
  disable: () => undefined,
  askOnRegister: () => undefined,
};

/**
 * One push registration for the page. The register button, the inbox row
 * and the Profile page's Notifications section all read it, so none of them
 * starts a second registration and turning push on from any of them saves
 * the token once. Mounted at the router root under the session; `uid` null
 * (signed out) keeps push off.
 */
export function PushRegistrationProvider({
  uid,
  children,
}: {
  uid: string | null;
  children: ReactNode;
}) {
  const value = usePushRegistrationState(uid);
  return createElement(PushRegistrationContext.Provider, { value }, children);
}

/** This page's push registration; see PushRegistrationProvider. */
export function usePushRegistration(): PushRegistration {
  return useContext(PushRegistrationContext) ?? NO_PUSH;
}
